import { VIEW, MODE, HOURS_FORMAT } from '@/components/base-components/components/date-picker/index.js';
import formatDate from '@/utils/format-date.ts';

export function getComparisonLevel(view) {
    switch (view) {
        case VIEW.YEAR:
            return 1;
        case VIEW.SCHEDULE:
        case VIEW.MONTH:
            return 3;
        case VIEW.WEEK:
        case VIEW.DAY:
        default:
            return 5;
    }
}

export function compareDates(first, second, level = 5) {
    if (!(first instanceof Date) || !(second instanceof Date)) return false;
    if (first.getFullYear() !== second.getFullYear()) return false;
    if (level >= 2 && first.getMonth() !== second.getMonth()) return false;
    if (level >= 3 && first.getDate() !== second.getDate()) return false;
    if (level >= 4 && first.getHours() !== second.getHours()) return false;
    if (level >= 5 && first.getMinutes() !== second.getMinutes()) return false;

    return true;
}

export function areDatesEqual(date, value, view = VIEW.MONTH, mode = MODE.SINGLE) {
    const level = getComparisonLevel(view);

    if (mode === MODE.SINGLE) return compareDates(date, value, level);

    if (mode === MODE.MULTIPLE) {
        if (!Array.isArray(value) || !value.length) return false;

        return value.some(item => compareDates(date, item, level));
    }

    if (mode === MODE.RANGE) return compareDates(date, value.start, level) || compareDates(date, value.end, level);

    return false;
}

function datesDiff(first, second, level = 5) {
    if (!(first instanceof Date) || !(second instanceof Date)) return 0;
    if (first.getFullYear() !== second.getFullYear()) return first.getFullYear() - second.getFullYear();
    if (level >= 2 && first.getMonth() !== second.getMonth()) return first.getMonth() - second.getMonth();
    if (level >= 3 && first.getDate() !== second.getDate()) return first.getDate() - second.getDate();
    if (level >= 4 && first.getHours() !== second.getHours()) return first.getHours() - second.getHours();
    if (level >= 5 && first.getMinutes() !== second.getMinutes()) return first.getMinutes() - second.getMinutes();

    return 0;
}

function isAfter(first, second, level) {
    return datesDiff(first, second, level) >= 0;
}

function isBefore(first, second, level) {
    return datesDiff(first, second, level) <= 0;
}

export function isDateBetween(date, range, view = VIEW.MONTH, mode = MODE.SINGLE) {
    if (mode !== MODE.RANGE) return false;
    if (!range?.start || !range?.end) return false;

    const level = getComparisonLevel(view);

    return isAfter(date, range.start, level) && isBefore(date, range.end, level);
}

export function format(value, mode = MODE.SINGLE, withTime = false, hoursFormat = HOURS_FORMAT.HOURS_24) {
    if (!value) return '';

    const options = {
        month: 'short',
        day:   'numeric',
        year:  'numeric',
        ...(withTime && {
            hourCycle: hoursFormat === HOURS_FORMAT.HOURS_24 ? 'h23' : 'h12',
            hour:      'numeric',
            minute:    'numeric',
        }),
    };

    switch (mode) {
        case MODE.SINGLE:
            return formatDate(value.toISOString(), options);

        case MODE.MULTIPLE:
            if (!Array.isArray(value)) return '';
            return value.map(date => formatDate(date.toISOString(), options)).join('; ');

        case MODE.RANGE:
            if (!value.start || !value.end) return '';
            return `${formatDate(value.start.toISOString(), options)} – ${formatDate(value.end.toISOString(), options)}`;

        default:
            return '';
    }
}
